<template>
    <div class="blog-post">
        <PageErrorFiveHundred
            v-if="error"
            :errors="responseErrors"
        >
            <ThButton
                :disabled="fetching"
                @click="executeQuery"
            >
                {{ t('globals.try_again') }}
            </ThButton>
        </PageErrorFiveHundred>

        <div v-else-if="post">
            <div class="header">
                <ProductBreadcrumbs
                    v-if="breadcrumbs.length"
                    :breadcrumbs
                />

                <h1 class="title">{{ title }}</h1>

                <!--TODO: Component! -->
                <div
                    v-if="author"
                    class="author"
                >
                    <div class="avatar">
                        <ThLink :to="routes.blog.author(author)">
                            <CmsImage
                                :data="author.image"
                                fit="cover"
                                is-eager
                            />
                        </ThLink>
                    </div>

                    <div class="description">
                        <div class="name">
                            <ThLink :to="routes.blog.author(author)">
                                {{ author.name }}
                            </ThLink>
                        </div>

                        <!-- TODO translate -->
                        <div class="meta">
                            Posted on

                            {{ dateFormatted }}

                            <span v-if="categories?.length">
                                in
                                <ThLink
                                    v-for="(category, key) in categories"
                                    :key="key"
                                    :to="routes.blog.category(category)"
                                    class="link"
                                >
                                    {{ category.name }}
                                </ThLink>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <CmsBlocks
                :data="blocks"
                first-block-has-eager-image
                page-type="post"
            />

            <div
                v-if="tags?.length"
                class="tags"
            >
                <!-- TODO translate -->
                <h4 class="title">Tags</h4>

                <ul class="list">
                    <li
                        v-for="(tag, key) in tags"
                        :key="key"
                        class="item"
                    >
                        <ThLink
                            :to="routes.blog.tag(tag)"
                            class="link"
                        >
                            {{ tag.name }}
                        </ThLink>
                    </li>
                </ul>
            </div>

            <div class="footer">
                <ul
                    v-if="currentUrl"
                    class="post-share"
                >
                    <li class="item facebook">
                        <a
                            :href="`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`"
                            class="link"
                            rel="nofollow"
                            target="_blank"
                            aria-label="Share on Facebook - opens in a new tab"
                        >
                            Facebook
                        </a>
                    </li>

                    <li class="item twitter">
                        <a
                            :href="`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                                title || ''
                            )}&via=taylorandhart&url=${encodeURIComponent(
                                currentUrl.toString()
                            )}`"
                            class="link"
                            rel="nofollow"
                            target="_blank"
                            aria-label="Share on Twitter - opens in a new tab"
                        >
                            Twitter
                        </a>
                    </li>
                </ul>

                <ScrollToTopButton />
            </div>

            <div
                v-if="relatedPosts"
                class="related-posts"
            >
                <div class="related-posts-wrapper">
                    <BlogPostPreview
                        v-for="relatedPost in relatedPosts"
                        :key="relatedPost.id"
                        :preview="relatedPost"
                    />
                </div>
            </div>
        </div>

        <PageErrorFourHundredFour v-else />
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSetStatus } from 'wucdbm-vite-vue-ssr'

import { useTranslator } from '@/i18n'
import { useMeta } from '@/composables/app/useMeta'
import { useRegionPlugin } from '@/plugins/region'
import { useRoutes } from '@/router/routes'
import { useResponseError } from '@/helpers/errors'
import { useBlogPostDateFormatter } from './composables/useBlogPostDate'
import { parseDate } from '@/utils/date'
import { useCurrentUrl } from '@/plugins/current-url.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useBlogPostQuery } from './BlogPost.generated.ts'

import type { Breadcrumb } from '@/components/ui/Breadcrumbs.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import BlogPostPreview from '@/components/cms/component/blog/BlogPostPreview.vue'
import PageErrorFourHundredFour from '@/components/PageErrorFourHundredFour.vue'
import CmsBlocks from '@/components/cms/CmsBlocks.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import ScrollToTopButton from '@/components/ui/button-utilities/ScrollToTopButton.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import ProductBreadcrumbs from '@/components/rings/ProductBreadcrumbs.vue'
import { useBlogFeedLink } from '@/views/blog/composables/useBlogFeedLink.ts'

const { slug = '' } = defineProps<{
    slug?: string | undefined
}>()

const { t } = useI18n()
const routes = useRoutes()

useBlogFeedLink()

const { locale } = useTranslator()
const currency = useCurrencyInput()
const { regionCode } = useRegionPlugin()

const variables = computed(() => {
    return {
        //TODO: Fix ?
        slug: slug,
        locale: locale.value,
        currency: currency.value,
        region: regionCode.value,
    }
})

const currentUrl = useCurrentUrl()

const response = useBlogPostQuery({ variables })
await response

const responseErrors = useResponseError(response)

const { fetching, data, error, stale, executeQuery } = response

const { setStatus } = useSetStatus()
if (!data.value?.postBySlug) {
    setStatus(404)
}

const post = computed(() => data.value?.postBySlug)
const title = computed(() => post.value?.title)
const author = computed(() => post.value?.author)

const date = computed(() => post.value && parseDate(post.value.date))
const dateFormatted = useBlogPostDateFormatter(date)
const categories = computed(() => post.value?.categories)
const blocks = computed(() => post.value?.blocks)
const tags = computed(() => post.value?.tags)
const relatedPosts = computed(() => post.value?.relatedPosts)

useMeta(
    {
        title: () => data.value?.postBySlug?.meta.title,
        description: () => data.value?.postBySlug?.meta.description,
        og: {
            type: 'article',
            title: () =>
                ((data.value?.postBySlug?.meta.title &&
                    `${data.value?.postBySlug?.meta.title} | `) ||
                    '') + data.value?.postBySlug?.title || '',
            description: () => data.value?.postBySlug?.meta.description,
            url: () =>
                data.value?.postBySlug
                    ? routes.blog.post(data.value?.postBySlug)
                    : undefined,
        },
        article: {
            author: () => data.value?.postBySlug?.author?.name,
            published_time: () => date.value ?? undefined,
        },
    },
    data,
    fetching,
    stale
)

// TODO translate
const breadcrumbs = computed((): Breadcrumb[] => [
    {
        title: 'Home',
        to: routes.home(),
    },
    {
        title: 'Blog',
        to: routes.blog.list(),
    },
    ...(title.value ? [{ title: title.value }] : []),
])
</script>

<style lang="scss" scoped>
:deep(.blog-menu) {
    padding: 0 20px;

    @include min-width(1024) {
        margin-top: 80px;
        padding-bottom: 20px;
        @include border-bottom;
    }
}

.header {
    width: 100%;
    max-width: 900px;
    margin: 40px auto;
    padding: 0 10px 0 20px;

    @include min-width(1024) {
        padding: 0;
    }

    .title {
        font-family: $parcel-font-stack;
        width: 100%;
        max-width: 800px;
        font-size: 2.25rem;
        line-height: 1.125em;
        letter-spacing: 0.01em;

        @include min-width(768) {
            font-size: 2.875rem;
        }
    }

    .author {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin: 40px 0 0;
        line-height: 15px;

        .avatar {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            overflow: hidden;
            margin: 0 10px 0 0;
            flex-shrink: 0;
        }

        .description {
            font-size: 0.875rem;
            text-transform: uppercase;
            color: $slate;
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-weight: 400;

            .meta {
                font-size: 0.625rem;
                color: $grey-400;

                .link {
                    &::after {
                        content: ', ';
                    }

                    @include hover() {
                        color: $slate;
                        text-decoration: underline;
                    }

                    &:last-child {
                        &::after {
                            display: none;
                        }
                    }
                }
            }
        }
    }
}

.tags {
    width: 680px;
    max-width: 100%;
    margin: 40px auto 20px;
    @include border-bottom;

    padding: 0 20px;
    // TODO consider if should be used. If not, tags should
    // probably come properly capitalized from the API,
    // i.e. 'Avril Lavigne' instead of 'avril lavigne'
    text-transform: capitalize;

    @include min-width(1024) {
        padding: 0;
    }

    .title {
        font-size: 1.25rem;
        margin: 0 0 10px;
    }

    .list {
        list-style: none;
        margin: 0 0 20px;
        display: flex;
        flex-wrap: wrap;
        font-family: $effra-font-stack;
        font-size: 0.8rem;
        font-weight: 400;
        color: $grey-500;

        .item {
            margin-right: 5px;

            &::after {
                content: ',';
            }

            &:last-child {
                &::after {
                    display: none;
                }
            }

            .link {
                @include hover() {
                    color: $slate;
                    text-decoration: underline;
                }
            }
        }
    }
}

.footer {
    width: 680px;
    max-width: 100%;
    display: flex;
    align-items: center;
    padding: 0 20px 20px;
    margin: 0 auto 40px;
    @include border-bottom;

    justify-content: space-between;
    font-family: $effra-font-stack;
    font-weight: 400;
    font-size: 0.8rem;
    line-height: 1.25rem;

    @include min-width(1024) {
        padding: 0 0 20px;
    }

    .post-share {
        display: flex;

        .item {
            .link {
                font-size: 0;
                width: 40px;
                height: 40px;
                line-height: 2.5rem;
                display: block;

                &::before {
                    font-family: $icon-font;
                    width: 40px;
                    height: 40px;
                    display: block;
                    text-align: center;
                    line-height: 2.5rem;
                    font-size: 1rem;
                }
            }

            &.facebook {
                .link {
                    &::before {
                        content: $icon-facebook;
                    }
                }
            }

            &.twitter {
                .link {
                    &::before {
                        content: $icon-twitter;
                    }
                }
            }

            &.link {
                .link {
                    &::before {
                        content: $icon-angle-down;
                    }
                }
            }
        }
    }

    .back-to-top {
        letter-spacing: 0.05em;
        text-transform: uppercase;
        cursor: default;

        &::before {
            font-family: $icon-font;
            content: $icon-angle-up;
            font-weight: 600;
            margin-right: 5px;
            line-height: 15px;
        }
    }
}

.related-posts {
    width: 100%;

    .related-posts-wrapper {
        width: 1140px;
        max-width: 100%;
        margin: 20px auto 40px;

        @include min-width(640) {
            padding: 0 20px 20px;
        }

        @include min-width(1024) {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            column-gap: 30px;
        }
    }
}
</style>
